/**
 * Scheduled Reports admin API module (email-me-this-report-on-a-cadence).
 *
 * Mounted at /api/accounts/:id/scheduled-reports and /api/scheduled-reports/:id.
 * Backend in turn-connect/internal/api/handlers/scheduled_report_handler.go.
 *
 * Extracted from the AdminService god class in services/auth.ts and converted
 * to the central web API client. Method names, signatures, and observable
 * behavior (error messages) mirror the originals so the composed
 * `adminService` export stays drop-in compatible.
 */

import { webApiClient, unwrap } from '../apiClient';
import type {
  ScheduledReport,
  ScheduledReportScope,
  CreateScheduledReportRequest,
  UpdateScheduledReportRequest,
} from '@turn-connect/shared';

/** One page of scheduled reports plus pagination metadata. */
export interface ScheduledReportsPage {
  items: ScheduledReport[];
  total: number;
  limit: number;
  offset: number;
  hasMore: boolean;
}

const DEFAULT_PAGE_SIZE = 20;

// Normalize the backend's paginated envelope ({items,total,has_more,…}) into a
// ScheduledReportsPage. Tolerates a bare array (older backend / unexpected
// shape) so the list never crashes on a payload mismatch.
function toScheduledReportsPage(json: any, limit: number, offset: number): ScheduledReportsPage {
  if (Array.isArray(json)) {
    return { items: json as ScheduledReport[], total: json.length, limit, offset, hasMore: false };
  }
  const items: ScheduledReport[] = Array.isArray(json?.items) ? json.items : [];
  const total = typeof json?.total === 'number' ? json.total : items.length;
  const hasMore = typeof json?.has_more === 'boolean' ? json.has_more : offset + items.length < total;
  return {
    items,
    total,
    limit: typeof json?.limit === 'number' ? json.limit : limit,
    offset: typeof json?.offset === 'number' ? json.offset : offset,
    hasMore,
  };
}

export async function listScheduledReports(
  accountId: string,
  scope?: ScheduledReportScope,
  limit: number = DEFAULT_PAGE_SIZE,
  offset: number = 0
): Promise<ScheduledReportsPage> {
  const params = new URLSearchParams();
  if (scope) params.set('scope', scope);
  params.set('limit', String(limit));
  params.set('offset', String(offset));
  const json = await unwrap<any>(
    webApiClient.get(`/accounts/${accountId}/scheduled-reports?${params.toString()}`),
    'Failed to fetch scheduled reports'
  );
  return toScheduledReportsPage(json, limit, offset);
}

/**
 * Cross-account list of schedules I authored. Powers the
 * "My Scheduled Reports" section on User Profile so multi-account
 * operators see their schedules without juggling the active
 * account selection.
 */
export async function listMyScheduledReports(limit: number = DEFAULT_PAGE_SIZE, offset: number = 0): Promise<ScheduledReportsPage> {
  const params = new URLSearchParams();
  params.set('limit', String(limit));
  params.set('offset', String(offset));
  const json = await unwrap<any>(
    webApiClient.get(`/users/me/scheduled-reports?${params.toString()}`),
    'Failed to fetch my scheduled reports'
  );
  return toScheduledReportsPage(json, limit, offset);
}

export async function createScheduledReport(accountId: string, data: CreateScheduledReportRequest): Promise<ScheduledReport> {
  return unwrap<ScheduledReport>(webApiClient.post(`/accounts/${accountId}/scheduled-reports`, data), 'Failed to create scheduled report');
}

export async function getScheduledReport(id: string): Promise<ScheduledReport> {
  return unwrap<ScheduledReport>(webApiClient.get(`/scheduled-reports/${id}`), 'Failed to fetch scheduled report');
}

export async function updateScheduledReport(id: string, data: UpdateScheduledReportRequest): Promise<ScheduledReport> {
  return unwrap<ScheduledReport>(webApiClient.put(`/scheduled-reports/${id}`, data), 'Failed to update scheduled report');
}

export async function deleteScheduledReport(id: string): Promise<void> {
  await unwrap(webApiClient.delete(`/scheduled-reports/${id}`), 'Failed to delete scheduled report');
}

export const reportsAdmin = {
  listScheduledReports,
  listMyScheduledReports,
  createScheduledReport,
  getScheduledReport,
  updateScheduledReport,
  deleteScheduledReport,
};
